import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import {
  firebaseConfigProjectId,
  firebaseConfigApiKey,
  firebaseConfigAuthDomain,
  firebaseConfigStorageBucket,
  firebaseConfigMessagingSenderId,
  firebaseConfigAppId,
  firebaseConfigMeasurementId,
  firebaseVapidKey
} from '../constants/configuration';

const firebaseConfig = {
  apiKey: firebaseConfigApiKey,
  authDomain: firebaseConfigAuthDomain,
  projectId: firebaseConfigProjectId,
  storageBucket: firebaseConfigStorageBucket,
  messagingSenderId: firebaseConfigMessagingSenderId,
  appId: firebaseConfigAppId,
  measurementId: firebaseConfigMeasurementId
};

const hasFirebase = !!firebaseConfigProjectId;

const firebaseApp = hasFirebase ? initializeApp(firebaseConfig) : null;
const messaging = hasFirebase && navigator.serviceWorker ? getMessaging(firebaseApp) : null;

const waitForServiceWorkerActivation = async (registration) => {
  if (registration.active) {
    return registration;
  }

  const worker = registration.installing || registration.waiting;
  if (!worker) {
    return navigator.serviceWorker.ready;
  }

  await new Promise((resolve, reject) => {
    const onStateChange = () => {
      if (worker.state === 'activated') {
        worker.removeEventListener('statechange', onStateChange);
        resolve();
      } else if (worker.state === 'redundant') {
        worker.removeEventListener('statechange', onStateChange);
        reject(new Error('Service worker became redundant before activation'));
      }
    };

    worker.addEventListener('statechange', onStateChange);
    onStateChange();
  });

  return navigator.serviceWorker.ready;
};

export const getNotificationToken = (setTokenFound) => {
  const resolveToken = async () => {
    if (!messaging || !firebaseVapidKey) {
      setTokenFound(null);
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setTokenFound(null);
        return;
      }

      const configParams = new URLSearchParams({
        apiKey: firebaseConfigApiKey || '',
        authDomain: firebaseConfigAuthDomain || '',
        projectId: firebaseConfigProjectId || '',
        storageBucket: firebaseConfigStorageBucket || '',
        messagingSenderId: firebaseConfigMessagingSenderId || '',
        appId: firebaseConfigAppId || '',
        measurementId: firebaseConfigMeasurementId || ''
      });

      const registration = await navigator.serviceWorker.register(
        `/firebase-messaging-sw.js?${configParams.toString()}`
      );
      const serviceWorkerRegistration = await waitForServiceWorkerActivation(registration);

      const currentToken = await getToken(messaging, {
        vapidKey: firebaseVapidKey,
        serviceWorkerRegistration
      });

      setTokenFound(currentToken || null);
    } catch (error) {
      console.error('Failed to get Firebase notification token', error);
      setTokenFound(null);
    }
  };

  resolveToken();
};

export const subscribeToForegroundMessages = (handler) => {
  if (!messaging) {
    return () => {};
  }

  return onMessage(messaging, (payload) => {
    handler(payload);
  });
};
